/**
 * SanPlumb — RightPanel del visor (Fase 3d)
 *
 * Cuatro tabs: INFO (elemento seleccionado), BOM (metrado del engine real),
 * IS.010 (alturas NPT + convención) y NORMAS (resumen normativo).
 *
 * El BOM se genera con `buildBOM()` del engine de Fase 2 sobre el SystemData
 * activo, agrupando por sistema/diámetro/spec.
 *
 * El análisis de presión IS.010 §2.3 vive en el paso Dotación (tarjeta de
 * presión estática por piso), no aquí — decisión de producto para que el
 * cálculo tenga contexto claro ("¿necesito hidroneumático/reductora?").
 *
 * Portado de `screens.jsx` (RightPanel) del diseño aprobado.
 */

import type { RightTab, SelectedElement } from '../../types/ui';
import type { Sanitary, SystemData } from '../../types';
import { buildBOM, type BomRow } from '../../engine/bom';
import { iconForBomRow } from '../Icon';

// Orden v1.15: BOM primero (es el deliverable real del proyecto).
// INFO queda como segundo tab (info contextual del elemento seleccionado).
const TABS: readonly RightTab[] = ['BOM', 'INFO', 'IS.010', 'NORMAS'];

export interface RightPanelProps {
  tab: RightTab;
  setTab: (t: RightTab) => void;
  selected: SelectedElement;
  system: SystemData;
}

export function RightPanel({ tab, setTab, selected, system }: RightPanelProps): JSX.Element {
  return (
    <div className="right-panel">
      <div className="rp-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            className={`rp-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="panel-scroll">
        {tab === 'INFO' && <InfoTab selected={selected} system={system} />}
        {tab === 'BOM' && <BomTab system={system} />}
        {tab === 'IS.010' && <Is010Tab />}
        {tab === 'NORMAS' && <NormasTab />}
      </div>
    </div>
  );
}

function InfoTab({
  selected,
  system,
}: {
  selected: SelectedElement;
  system: SystemData;
}): JSX.Element {
  if (selected.kind === 'none') {
    return (
      <div className="lp-section">
        <div className="lp-h">▸ Sistema</div>
        <div className="pipe-row">
          <span className="t-af">AF</span>
          <span>Tramos agua fría</span>
          <span className="mono">{system.pipes.length}</span>
        </div>
        <div className="pipe-row">
          <span className="t-ac">AC</span>
          <span>Tramos agua caliente</span>
          <span className="mono">{system.pipes_ac.length}</span>
        </div>
        <div className="pipe-row">
          <span className="mono">+</span>
          <span>Accesorios</span>
          <span className="mono">
            {system.accessories.length + system.accessories_ac.length}
          </span>
        </div>
        <div className="pipe-row">
          <span className="mono">⌂</span>
          <span>Aparatos</span>
          <span className="mono">{system.sanitaries.length}</span>
        </div>
        <p className="h-sub" style={{ marginTop: 14 }}>
          Haz click en una tubería, accesorio o aparato del visor para ver su detalle.
        </p>
      </div>
    );
  }

  if (selected.kind === 'pipe') {
    return (
      <div className="lp-section">
        <div className="lp-h">▸ Tubería {selected.sys.toUpperCase()}</div>
        <div className="pipe-row">
          <span className={selected.sys === 'ac' ? 't-ac' : 't-af'}>
            {selected.sys.toUpperCase()}
          </span>
          <span>Sistema</span>
          <span className="mono">{selected.sys === 'ac' ? 'CPVC' : 'PVC-P'}</span>
        </div>
        <div className="pipe-row">
          <span className="mono">Ø</span>
          <span>Calibre</span>
          <span className="mono">{selected.gauge === 'thick' ? '¾"' : '½"'}</span>
        </div>
        <div className="pipe-row">
          <span className="mono">L</span>
          <span>Longitud</span>
          <span className="mono">{selected.len.toFixed(2)} m</span>
        </div>
      </div>
    );
  }

  if (selected.kind === 'apar') {
    const sanitary = findSelectedSanitary(system.sanitaries, selected.label);
    return (
      <div className="lp-section">
        <div className="lp-h">▸ Aparato</div>
        <div className="pipe-row">
          <span className="mono">⌂</span>
          <span>Tipo</span>
          <span className="mono">{selected.label}</span>
        </div>
        {sanitary && (
          <>
            <div className="pipe-row">
              <span className="mono">W</span>
              <span>Ancho</span>
              <span className="mono">{toCm(sanitary.w)} cm</span>
            </div>
            <div className="pipe-row">
              <span className="mono">D</span>
              <span>Fondo</span>
              <span className="mono">{toCm(sanitary.d)} cm</span>
            </div>
            <div className="pipe-row">
              <span className="mono">H</span>
              <span>Alto</span>
              <span className="mono">{toCm(sanitary.h)} cm</span>
            </div>
            <div className="pipe-row">
              <span className="mono">AF</span>
              <span>Conexión</span>
              <span className="mono">{sanitary.conn_af}</span>
            </div>
            {sanitary.has_ac && (
              <div className="pipe-row">
                <span className="mono t-ac">AC</span>
                <span>Conexión</span>
                <span className="mono">{sanitary.conn_ac}</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // fitting / valve / reduction
  return (
    <div className="lp-section">
      <div className="lp-h">▸ {selected.kind === 'valve' ? 'Válvula' : selected.kind === 'reduction' ? 'Reducción' : 'Accesorio'}</div>
      <AccessoryPreview kind={selected.kind} sys={selected.sys} label={selected.label} />
      <div className="pipe-row">
        <span className={selected.sys === 'ac' ? 't-ac' : 't-af'}>{selected.sys.toUpperCase()}</span>
        <span>Tipo</span>
        <span className="mono">{selected.label}</span>
      </div>
    </div>
  );
}

function BomTab({ system }: { system: SystemData }): JSX.Element {
  const rows = buildBOM(system);
  const groups: readonly { key: string; label: string; swatch: string; rows: BomRow[] }[] = [
    { key: 'AF', label: 'AF', swatch: 'af', rows: rows.filter((r) => r.sys === 'AF') },
    { key: 'AC', label: 'AC', swatch: 'ac', rows: rows.filter((r) => r.sys === 'AC') },
    {
      key: 'SAN',
      label: 'Sanitarios',
      swatch: 'san',
      rows: rows.filter((r) => r.cat === 'sanitario'),
    },
  ];
  return (
    <div className="lp-section">
      <div className="lp-h">▸ Metrado (BOM)</div>
      {groups.map((g) => (
        <div className="bom-group" key={g.key}>
          <div className="bom-h">
            <span className={`swatch ${g.swatch}`} />
            <span>{g.label}</span>
          </div>
          {g.rows.map((r, i) => (
            <div className="bom-row icon" key={`${g.key}-${i}`}>
              <span className="bom-ic" style={{ color: r.dot }}>
                {iconForBomRow(r.cat, r.label)}
              </span>
              <span className="lbl">{r.label}</span>
              <span className="mono">{r.qty}</span>
            </div>
          ))}
        </div>
      ))}
      <p className="h-sub" style={{ marginTop: 12, fontSize: 9 }}>
        Tuberías con factor de desperdicio 1.15. Exportable a XLSX desde el TopBar.
      </p>
    </div>
  );
}

function Is010Tab(): JSX.Element {
  const rows: readonly [string, string][] = [
    ['AF (agua fría)', 'DERECHA del eje (+10cm)'],
    ['AC (agua caliente)', 'IZQUIERDA del eje (−10cm)'],
    ['Separación AF/AC (uso)', '20 cm (10+10)'],
    ['Separación AF/AC (muro)', '≥ 3 cm — IS.010 §2.3.i'],
    ['Inodoro (excepción)', 'AF a IZQUIERDA (−15cm)'],
    ['Tubo de abasto', '30–35 cm (flexible)'],
    ['Salida AF', 'Codo galvanizado ½"'],
    ['Salida AC', 'Adaptador CPVC + niple galv.'],
  ];
  return (
    <div className="lp-section">
      <div className="lp-h">▸ Convención IS.010</div>
      <table className="rnt">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td>{k}</td>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NormasTab(): JSX.Element {
  const items: readonly [string, string][] = [
    ['§2.2', 'Dotación de agua fría por tipo de edificación'],
    ['§2.3', 'Diámetros, velocidades máximas y presiones'],
    ['§2.4', 'Almacenamiento: cisterna ≥¾, tanque ≥⅓'],
    ['§3.2', 'Dotación de agua caliente por dormitorios'],
    ['§3.4', 'Capacidad de terma (⅕ de la dotación AC)'],
  ];
  return (
    <div className="lp-section">
      <div className="lp-h">▸ Normas aplicadas</div>
      {items.map(([s, d]) => (
        <div className="pipe-row" key={s}>
          <span className="mono t-af">{s}</span>
          <span className="norma-desc">{d}</span>
        </div>
      ))}
    </div>
  );
}

function findSelectedSanitary(sanitaries: readonly Sanitary[], label: string): Sanitary | undefined {
  return sanitaries.find((s) => s.type === label || s.subtype === label || label.includes(s.type));
}

function toCm(meters: number): number {
  return Math.round(meters * 100);
}

function AccessoryPreview({
  kind,
  sys,
  label,
}: {
  kind: 'fitting' | 'valve' | 'reduction';
  sys: 'af' | 'ac';
  label: string;
}): JSX.Element {
  const color = sys === 'ac' ? '#FF4040' : '#00C2FF';
  const accent = kind === 'valve' ? '#FFB800' : kind === 'reduction' ? '#8B5CF6' : color;
  const lower = label.toLowerCase();
  const shape = kind === 'valve' ? 'valve' : kind === 'reduction' ? 'reduction' : lower.includes('tee') ? 'tee' : 'elbow';

  return (
    <div className={`accessory-preview ${sys}`}>
      <svg className="acc-iso-svg" viewBox="0 0 120 82" aria-hidden="true">
        <defs>
          <linearGradient id={`accGrad-${sys}-${shape}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={accent} />
          </linearGradient>
        </defs>
        <path d="M 18 58 L 50 40 L 84 58" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="2" />
        {shape === 'tee' && (
          <>
            <path d="M 20 48 L 62 24 L 100 46" fill="none" stroke={`url(#accGrad-${sys}-${shape})`} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M 62 24 L 62 62" fill="none" stroke={`url(#accGrad-${sys}-${shape})`} strokeWidth="10" strokeLinecap="round" />
            <circle cx="62" cy="24" r="7" fill={accent} />
          </>
        )}
        {shape === 'elbow' && (
          <>
            <path d="M 26 54 L 62 34 Q 84 22 94 42 L 102 58" fill="none" stroke={`url(#accGrad-${sys}-${shape})`} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="76" cy="34" r="7" fill={accent} />
          </>
        )}
        {shape === 'valve' && (
          <>
            <path d="M 18 48 L 102 48" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" />
            <rect x="50" y="35" width="20" height="26" rx="4" fill={accent} />
            <path d="M 43 28 H 77" stroke={accent} strokeWidth="6" strokeLinecap="round" />
            <path d="M 60 35 V 28" stroke={accent} strokeWidth="5" strokeLinecap="round" />
          </>
        )}
        {shape === 'reduction' && (
          <>
            <path d="M 18 48 H 54" stroke={color} strokeWidth="12" strokeLinecap="round" />
            <path d="M 66 48 H 102" stroke={color} strokeWidth="8" strokeLinecap="round" />
            <path d="M 52 42 L 68 44 L 68 52 L 52 54 Z" fill={accent} />
          </>
        )}
      </svg>
      <span>{shape === 'tee' ? 'Tee isométrica' : shape === 'valve' ? 'Válvula isométrica' : shape === 'reduction' ? 'Reducción isométrica' : 'Codo isométrico'}</span>
    </div>
  );
}
